<script setup>
import { useAlert } from 'dashboard/composables';
import { computed, onBeforeMount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters, useStore } from 'dashboard/composables/store';

import AddLabel from './AddLabel.vue';
import EditLabel from './EditLabel.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Table from 'dashboard/components-next/table/Table.vue';

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();

const loading = ref({});
const showAddPopup = ref(false);
const showEditPopup = ref(false);
const showDeleteConfirmationPopup = ref(false);
const selectedLabel = ref({});

const records = computed(() => getters['labels/getLabels'].value);
const uiFlags = computed(() => getters['labels/getUIFlags'].value);

const deleteMessage = computed(() => ` ${selectedLabel.value.title}?`);

const openAddPopup = () => {
  showAddPopup.value = true;
};
const hideAddPopup = () => {
  showAddPopup.value = false;
};

const openEditPopup = response => {
  showEditPopup.value = true;
  selectedLabel.value = response;
};
const hideEditPopup = () => {
  showEditPopup.value = false;
};

const openDeletePopup = response => {
  showDeleteConfirmationPopup.value = true;
  selectedLabel.value = response;
};
const closeDeletePopup = () => {
  showDeleteConfirmationPopup.value = false;
};

const deleteLabel = async id => {
  try {
    await store.dispatch('labels/delete', id);
    useAlert(t('LABEL_MGMT.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    const errorMessage =
      error?.message || t('LABEL_MGMT.DELETE.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  } finally {
    loading.value[selectedLabel.value.id] = false;
  }
};

const confirmDeletion = () => {
  loading.value[selectedLabel.value.id] = true;
  closeDeletePopup();
  deleteLabel(selectedLabel.value.id);
};

// Columns for the canonical Table primitive. The legacy markup also had an
// unlabeled "Actions" column — we now declare it explicitly.
const columns = computed(() => [
  { key: 'name',        label: t('LABEL_MGMT.LIST.TABLE_HEADER.NAME'), width: '14rem' },
  { key: 'description', label: t('LABEL_MGMT.LIST.TABLE_HEADER.DESCRIPTION') },
  { key: 'color',       label: t('LABEL_MGMT.LIST.TABLE_HEADER.COLOR'), width: '10rem' },
  { key: 'actions',     label: '', align: 'right', width: '7rem' },
]);

onBeforeMount(() => {
  store.dispatch('labels/get');
});
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('LABEL_MGMT.LOADING')"
    :no-records-found="!records.length"
    :no-records-message="$t('LABEL_MGMT.LIST.404')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('LABEL_MGMT.HEADER')"
        :description="$t('LABEL_MGMT.DESCRIPTION')"
        :link-text="$t('LABEL_MGMT.LEARN_MORE')"
        feature-name="labels"
      >
        <template #actions>
          <Button
            icon="i-lucide-circle-plus"
            :label="$t('LABEL_MGMT.HEADER_BTN_TXT')"
            @click="openAddPopup"
          />
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <Table :columns="columns" :rows="records" :row-key="row => row.title">
        <template #cell-name="{ row: label }">
          <span class="mb-1 font-medium break-words text-n-slate-12">
            {{ label.title }}
          </span>
        </template>
        <template #cell-description="{ row: label }">
          {{ label.description }}
        </template>
        <template #cell-color="{ row: label }">
          <div class="flex items-center">
            <span
              class="w-4 h-4 ltr:mr-1 rtl:ml-1 border border-solid rounded border-n-weak"
              :style="{ backgroundColor: label.color }"
            />
            {{ label.color }}
          </div>
        </template>
        <template #cell-actions="{ row: label }">
          <div class="flex gap-1 justify-end">
            <Button
              v-tooltip.top="$t('LABEL_MGMT.FORM.EDIT')"
              icon="i-lucide-pen"
              slate
              xs
              faded
              :is-loading="loading[label.id]"
              @click="openEditPopup(label)"
            />
            <Button
              v-tooltip.top="$t('LABEL_MGMT.FORM.DELETE')"
              icon="i-lucide-trash-2"
              xs
              ruby
              faded
              :is-loading="loading[label.id]"
              @click="openDeletePopup(label)"
            />
          </div>
        </template>
      </Table>
    </template>

    <woot-modal v-model:show="showAddPopup" :on-close="hideAddPopup">
      <AddLabel @close="hideAddPopup" />
    </woot-modal>

    <woot-modal v-model:show="showEditPopup" :on-close="hideEditPopup">
      <EditLabel :selected-response="selectedLabel" @close="hideEditPopup" />
    </woot-modal>

    <woot-delete-modal
      v-model:show="showDeleteConfirmationPopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('LABEL_MGMT.DELETE.CONFIRM.TITLE')"
      :message="$t('LABEL_MGMT.DELETE.CONFIRM.MESSAGE')"
      :message-value="deleteMessage"
      :confirm-text="$t('LABEL_MGMT.DELETE.CONFIRM.YES')"
      :reject-text="$t('LABEL_MGMT.DELETE.CONFIRM.NO')"
    />
  </SettingsLayout>
</template>
